import { cn } from '../../utils/cn'

export interface SkeletonProps {
  className?: string
  /** Render N stacked lines. */
  lines?: number
  circle?: boolean
}

export function Skeleton({ className, lines, circle }: SkeletonProps) {
  if (lines && lines > 1) {
    return (
      <div className="flex flex-col gap-2" aria-hidden>
        {Array.from({ length: lines }).map((_, i) => (
          <div key={i} className={cn('animate-pulse bg-surface-sunken rounded h-3', i === lines - 1 ? 'w-2/3' : 'w-full', className)} />
        ))}
      </div>
    )
  }
  return <div aria-hidden className={cn('animate-pulse bg-surface-sunken', circle ? 'rounded-full' : 'rounded', className ?? 'h-4 w-full')} />
}

/** Table-shaped skeleton for DataTable loading state. */
export function SkeletonTable({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="divide-y divide-line" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4 px-4 py-3">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={cn('h-3', c === 0 ? 'w-1/6' : 'flex-1')} />
          ))}
        </div>
      ))}
    </div>
  )
}

/** Card/section skeleton. */
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn('bg-surface rounded-lg border border-line p-4', className)} aria-busy="true">
      <Skeleton className="h-4 w-1/3 mb-3" />
      <Skeleton lines={3} />
    </div>
  )
}

/** Full-page fallback for React.lazy Suspense. */
export function PageSkeleton() {
  return (
    <div className="p-6 space-y-4 animate-fade-in" aria-busy="true" aria-label="Loading page">
      <Skeleton className="h-6 w-48" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SkeletonCard /><SkeletonCard /><SkeletonCard />
      </div>
      <div className="bg-surface rounded-lg border border-line"><SkeletonTable /></div>
    </div>
  )
}

export default Skeleton
